'use client'

import { getAppKit, wagmiAdapter } from '@/config/appkit'
import { useLoginWithSiwe } from '@privy-io/react-auth'
import { useAppKit, useDisconnect } from '@reown/appkit/react'
import { getAccount, signMessage, watchAccount } from '@wagmi/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Address } from 'viem'

type SiweStep = 'idle' | 'connecting' | 'signing' | 'verifying'

export function useSiweAuth() {
	const [step, setStep] = useState<SiweStep>('idle')
	const [error, setError] = useState<string | null>(null)
	const { disconnect } = useDisconnect()
	const { open } = useAppKit()
	const { generateSiweMessage, loginWithSiwe } = useLoginWithSiwe()

	// Monotonic attempt id — on cancel we bump it so any in-flight connect()
	// awaits bail out when they resume. Also used to cancel the watchAccount
	// subscription immediately.
	const attemptIdRef = useRef(0)
	const pendingCancelRef = useRef<(() => void) | null>(null)

	useEffect(() => {
		getAppKit()
	}, [])

	const connect = useCallback(async (): Promise<boolean> => {
		const myAttemptId = ++attemptIdRef.current
		const isCancelled = () => attemptIdRef.current !== myAttemptId

		setError(null)
		setStep('connecting')

		try {
			// 1. Get an EVM wallet connection — reuse an existing one if wagmi
			//    already has one, otherwise open the AppKit modal and wait for
			//    the user to pick one.
			const { address, chainId } = await ensureWalletConnection(open, pendingCancelRef)
			if (isCancelled()) return false

			// 2. Ask Privy to mint the EIP-4361 message (it owns the nonce).
			const message = await generateSiweMessage({
				address,
				chainId: `eip155:${chainId}`,
			})
			if (isCancelled()) return false

			// 3. Ask the connected wallet to sign Privy's message.
			setStep('signing')
			const signature = await signMessage(wagmiAdapter.wagmiConfig, { message })
			if (isCancelled()) return false

			// 4. Hand the signed message back to Privy to establish the session
			//    natively (no custom JWT). Optional walletClientType/connectorType
			//    are omitted — the d.ts marks them optional and we can't cleanly
			//    source the exact connector identifiers from AppKit/wagmi here.
			setStep('verifying')
			await loginWithSiwe({ signature, message })
			if (isCancelled()) return false

			// 5. Keep AppKit's wagmi connected so the LI.FI deposit widget can
			//    reuse the signing wallet without prompting the user to
			//    reconnect. Privy owns the session from here; wagmi just stays
			//    around as the source wallet for cross-chain bridging.
			setStep('idle')
			return true
		} catch (err) {
			if (isCancelled()) return false
			setStep('idle')
			// User-rejected signatures aren't a real error worth surfacing.
			if (isUserRejection(err)) return false
			setError(err instanceof Error ? err.message : 'Failed to sign in')
			return false
		}
	}, [open, generateSiweMessage, loginWithSiwe])

	const cancel = useCallback(() => {
		// Invalidate any in-flight attempt and tear down the pending wallet
		// watcher so the hook is fresh on the next open.
		attemptIdRef.current++
		pendingCancelRef.current?.()
		pendingCancelRef.current = null
		setStep('idle')
		setError(null)
	}, [])

	const cleanup = useCallback(async () => {
		cancel()
		await disconnect()
	}, [cancel, disconnect])

	return {
		connect,
		cancel,
		disconnect: cleanup,
		isPending: step !== 'idle',
		step,
		error,
		clearError: () => setError(null),
	}
}

async function ensureWalletConnection(
	openAppKit: () => Promise<unknown>,
	pendingCancelRef: { current: (() => void) | null }
): Promise<{ address: Address; chainId: number }> {
	const existing = getAccount(wagmiAdapter.wagmiConfig)
	if (existing.isConnected && existing.address && existing.chainId) {
		return { address: existing.address, chainId: existing.chainId }
	}

	await openAppKit()

	return new Promise((resolve, reject) => {
		const cleanup = () => {
			clearTimeout(timeout)
			unwatch()
			pendingCancelRef.current = null
		}

		const timeout = setTimeout(() => {
			cleanup()
			reject(new Error('Wallet connection timed out'))
		}, 120_000)

		const unwatch = watchAccount(wagmiAdapter.wagmiConfig, {
			onChange(account) {
				if (account.isConnected && account.address && account.chainId) {
					cleanup()
					resolve({ address: account.address, chainId: account.chainId })
				}
			},
		})

		// Expose a cancel hook so useSiweAuth.cancel() can tear this down
		// when the user closes the auth dialog mid-flow.
		pendingCancelRef.current = () => {
			cleanup()
			reject(new Error('Cancelled'))
		}
	})
}

function isUserRejection(err: unknown): boolean {
	if (!(err instanceof Error)) return false
	const msg = err.message.toLowerCase()
	return (
		msg.includes('user rejected') ||
		msg.includes('user denied') ||
		msg.includes('rejected the request')
	)
}
